import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge, Badge } from '@/components/ui/Badge';
import type { ClientRecord } from '@/api/clients';
import { useAuthStore } from '@/stores/auth';
import {
    useClientEligibilityHistory,
    type EligibilityCheck,
} from '@/api/eligibility';
import { EligibilityDialog } from './EligibilityDialog';

/*
 * Eligibility section — renders under the Authorizations table on the
 * Auth & Eligibility tab. Shows the latest verification summary and
 * the full history for this client. The "Verify Eligibility" button
 * today opens the manual-entry dialog; when Office Ally is wired up
 * the same button will prefer an automated 270/271 when OA credentials
 * are configured, and fall back to the manual dialog otherwise.
 */

const CHECKED_DATE_FMT = new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
});

function fmtChecked(iso: string): string {
    const t = Date.parse(iso);
    return Number.isNaN(t) ? '—' : CHECKED_DATE_FMT.format(new Date(t));
}

function latestFor(rows: EligibilityCheck[] | undefined): EligibilityCheck | null {
    if (!rows || rows.length === 0) return null;
    // Server returns rows newest-first; trust the order.
    return rows[0];
}

export function ClientEligibilitySection({ client: c }: { client: ClientRecord }) {
    const fullName = `${c.first} ${c.last}`.trim();
    const canRun = useAuthStore((s) => s.hasPermission('run_eligibility'));
    const canView = useAuthStore((s) => s.hasPermission('view_eligibility'));
    const [dialogOpen, setDialogOpen] = useState(false);

    const { data: history, isLoading } = useClientEligibilityHistory(
        canView ? fullName : undefined,
    );
    const latest = latestFor(history);

    const payer    = latest?.payer    || c.ins1?.payer || '—';
    const memberId = latest?.memberId || c.ins1?.id    || '—';

    return (
        <>
            <Card className="p-0 overflow-hidden mt-4">
                <CardHeader className="px-5 pt-5 flex items-start justify-between">
                    <div>
                        <CardTitle>Eligibility</CardTitle>
                        <CardSubtitle>
                            Verify coverage before sessions · history retained per client
                        </CardSubtitle>
                    </div>
                    {canRun && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => setDialogOpen(true)}>
                            <ShieldCheck className="w-3.5 h-3.5" /> Verify Eligibility
                        </Button>
                    )}
                </CardHeader>

                {!canView ? (
                    <p className="px-5 pb-5 text-sm text-text-subtle">
                        You don't have permission to view eligibility history.
                    </p>
                ) : (
                    <>
                        <dl className="px-5 pb-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                            <SummaryItem label="Payer (Primary)" value={payer} />
                            <SummaryItem label="Member ID" value={<span className="font-mono text-xs">{memberId}</span>} />
                            <SummaryItem
                                label="Coverage Status"
                                value={latest ? <StatusBadge status={latest.status || 'Unknown'} /> : '—'}
                            />
                            <SummaryItem
                                label="Last Verified"
                                value={latest ? fmtChecked(latest.checked) : '—'}
                            />
                            <SummaryItem label="Deductible" value={latest?.deductible || '—'} />
                            <SummaryItem label="Copay" value={latest?.copay || '—'} />
                            <SummaryItem
                                label="ABA Auth on File"
                                value={
                                    latest
                                        ? (latest.authOnFile
                                            ? <Badge variant="success">On File</Badge>
                                            : <Badge variant="error">Not on File</Badge>)
                                        : '—'
                                }
                            />
                            <SummaryItem label="Verified By" value={latest?.checkedBy || '—'} />
                        </dl>

                        <div className="border-t border-border">
                            <div className="px-5 py-2 text-2xs uppercase tracking-wider text-text-subtle font-bold">
                                Verification History
                            </div>
                            {isLoading ? (
                                <p className="px-5 pb-5 text-sm text-text-subtle">Loading…</p>
                            ) : !history || history.length === 0 ? (
                                <p className="px-5 pb-5 text-sm text-text-subtle">
                                    No eligibility checks recorded for this client.
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-5 py-2 font-bold">Checked</th>
                                                <th className="px-3 py-2 font-bold">Payer</th>
                                                <th className="px-3 py-2 font-bold">Status</th>
                                                <th className="px-3 py-2 font-bold">Deductible</th>
                                                <th className="px-3 py-2 font-bold">Copay</th>
                                                <th className="px-3 py-2 font-bold">Auth</th>
                                                <th className="px-5 py-2 font-bold">Verified By</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map((row) => (
                                                <tr key={row.id} className="border-b border-border last:border-b-0">
                                                    <td className="px-5 py-2 text-text-muted whitespace-nowrap">
                                                        {fmtChecked(row.checked)}
                                                    </td>
                                                    <td className="px-3 py-2 text-text">{row.payer || '—'}</td>
                                                    <td className="px-3 py-2">
                                                        <StatusBadge status={row.status || 'Unknown'} />
                                                    </td>
                                                    <td className="px-3 py-2 text-text-muted">{row.deductible || '—'}</td>
                                                    <td className="px-3 py-2 text-text-muted">{row.copay || '—'}</td>
                                                    <td className="px-3 py-2">
                                                        {row.authOnFile
                                                            ? <Badge variant="success">On File</Badge>
                                                            : <Badge variant="neutral">—</Badge>}
                                                    </td>
                                                    <td className="px-5 py-2 text-text-muted">{row.checkedBy || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Card>

            {canRun && (
                <EligibilityDialog
                    client={c}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                />
            )}
        </>
    );
}

function SummaryItem({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div>
            <dt className="text-2xs uppercase tracking-wider text-text-subtle font-bold">{label}</dt>
            <dd className="mt-0.5 text-text">{value}</dd>
        </div>
    );
}
